import {push} from "react-router-redux"
import {IFileInfos} from "../../../core/actions"
import {
    completeDeclined,
    FieldType,
    formatGetProductResponse,
    IFileDetails,
    ISupplierReference,
    ITranslations,
    LockStatus,
    permanentFormIds, sanitizeProduct,
} from "../../../models"
import {ITechField, ITechRawData} from "../../../models/bo"
import IRawCaratech from "../../../models/product/caratech"
import {Action} from "../../../utils/combine-reducer"
import {ITreeNode} from "../../../utils/tree/interfaces"
import {IPreviousVersions} from "../_reducers/form-id-state"
import ICreateField, {
    IDropdownOption,
} from "../_reducers/_utils/product-form-types"

export interface IProductFormActionTypesAndValues {
    "@@init": never
    CLOSE_FORM_ID: never
    RESET_FORM_ID: never
    REFETCH_PRODUCT_DATA: never
    UPDATE_DECLINATION_LIST: IDropdownOption[]
    UPDATE_DECLINATION_OPTIONS: IDropdownOption[]
    STORE_PRODUCT: IStoreProductPayload
    STORE_PREVIOUS_VERSION: IStorePreviousVersionPayload
    SELECT_PREVIOUS_VERSION: string
    START_FORM_LOADING: never
    STOP_FORM_LOADING: never
    START_FORM_SUBMITTING: never
    STOP_FORM_SUBMITTING: never
    RESET_FORM: IResetFormPayload
    STORE_TECH_FIELDS: IStoreTechFieldsPayload
    CHANGE_FIELD_VALUE: IChangeFieldPayload
    CREATE_FILE: ICreateFilePayload
    DELETE_FILE: IDeleteFilePayload
    RESTORE_FILE: IRestoreFilePayload
    CHANGE_FILE_POSITION: IChangeFilePositionPayload
    SET_ERROR_MESSAGE: ISetErrorMessagePayload
    CHANGE_MODIFIED_STATE: IChangeModifiedStatePayload
    CHANGE_FIELD_LABEL: IChangeFieldLabelPayload
}

interface IChangeModifiedStatePayload {
    code: string
    modified: boolean
}

interface IStoreProductPayload {
    product: ISupplierReference
    values: { [fieldName: string]: any }
}

interface IStorePreviousVersionPayload {
    previousVersion: ISupplierReference
    date: string
}

interface IStoreTechFieldsPayload {
    fields: Array<ICreateField<any>>
    rawData: ITechRawData[]
    techValues?: IRawCaratech[]
    modified?: boolean
}

export interface IChangeFieldPayload {
    code: string
    fieldValue: any
    errorMessage: string
    fieldType: FieldType
    lock: LockStatus
    overrideDefaultValue: boolean
}

export interface ICreateFilePayload {
    code: string
    file: File
    blob: string | ArrayBuffer | null
    name: string
    isNew: boolean
}

export interface IDeleteFilePayload {
    code: string
    fileInfos: IFileDetails
}

export interface IRestoreFilePayload {
    code: string
    fileInfos: IFileInfos
}

export interface IChangeFilePositionPayload {
    code: string
    fileName: string
    increment: boolean
}

interface ISetErrorMessagePayload {
    [fieldName: string]: string
}

interface IResetFormPayload {
    locked: string[]
    previousVersions: IPreviousVersions
}

interface IChangeFieldLabelPayload {
    [fieldName: string]: string
}

export const getActions = (
    dispatch: (action: Action<IProductFormActionTypesAndValues>) => void
) => ({
    _initFormId: (formId: string) =>
        dispatch({
            type: "@@init",
            formId,
        }),
    _refetchProductData: (formId: string) =>
        dispatch({
            type: "REFETCH_PRODUCT_DATA",
            formId,
        }),
    _closeFormId: (formId: string) => {
        // @ts-ignore
        dispatch(push("/product_list"))
        dispatch({
            type: "CLOSE_FORM_ID",
            formId,
        })
    },
    _resetFormId: (formId: string) =>
        dispatch({
            type: "RESET_FORM_ID",
            formId,
        }),
    _startLoading: () => dispatch({type: "START_FORM_LOADING"}),
    _stopLoading: () => dispatch({type: "STOP_FORM_LOADING"}),
    _startSubmitting: () => dispatch({type: "START_FORM_SUBMITTING"}),
    _stopSubmitting: () => dispatch({type: "STOP_FORM_SUBMITTING"}),
    _updateDeclinationList: (data: IDropdownOption[]) =>
        dispatch({type: "UPDATE_DECLINATION_LIST", value: data}),
    _updateDeclinationOptions: (data: IDropdownOption[]) =>
        dispatch({type: "UPDATE_DECLINATION_OPTIONS", value: data}),
    _storeDeclined: (payload: IStoreDeclinedArguments) => {
        const product = completeDeclined(
            payload.topRef,
            payload.topRef?.declined?.find(
                (x) => x.droopyId + "" === payload.declinedDroopyId + ""
            ) as any
        )
        const values = formatGetProductResponse({...payload, product: product})
        dispatch({
            type: "STORE_PRODUCT",
            value: {product: product, values},
        })
    },
    _storeProduct: (formId: string, payload: IStoreProductArguments) => {
        const values = formatGetProductResponse(payload)
        dispatch({
            type: "STORE_PRODUCT",
            formId,
            value: {product: payload.product, values},
        })
    },
    _sanitizeProduct: (formId: string, payload: IStoreProductArguments & {
        techFieldsRawData: ITechRawData[]
    }) => {
        let supplierRef = formatGetProductResponse(payload)
        supplierRef = sanitizeProduct(supplierRef, payload.techFieldsRawData)
        const product = sanitizeProduct(payload.product, payload.techFieldsRawData)
        dispatch({
            type: "STORE_PRODUCT",
            formId,
            value: {product: product, values: supplierRef},
        })
    },
    _storePreviousVersion: (
        formId: string,
        previousVersion: ISupplierReference,
        date: string
    ) => {
        dispatch({
            type: "STORE_PREVIOUS_VERSION",
            formId,
            value: {previousVersion, date},
        })
    },
    _selectPreviousVersion: (formId: string, version: string) =>
        dispatch({
            type: "SELECT_PREVIOUS_VERSION",
            formId,
            value: version,
        }),
    _changeModifiedState: (payload: IChangeModifiedStatePayload) =>
        dispatch({
            type: "CHANGE_MODIFIED_STATE",
            formId: permanentFormIds.MULTI_MODIF,
            value: payload,
        }),
    _storeTechFields: (formId: string, payload: IStoreTechFieldsPayload) =>
        dispatch({
            type: "STORE_TECH_FIELDS",
            formId,
            value: payload,
        }),
    _onChangeField: (formId: string, payload: IChangeFieldPayload) =>
        dispatch({
            type: "CHANGE_FIELD_VALUE",
            formId,
            value: payload,
        }),
    _createFile: (formId: string, payload: ICreateFilePayload) =>
        dispatch({
            type: "CREATE_FILE",
            formId,
            value: payload,
        }),
    _deleteFile: (formId: string, payload: IDeleteFilePayload) =>
        dispatch({
            type: "DELETE_FILE",
            formId,
            value: payload,
        }),
    _restoreFile: (formId: string, payload: IRestoreFilePayload) =>
        dispatch({
            type: "RESTORE_FILE",
            formId,
            value: payload,
        }),
    _changeFilePosition: (formId: string, payload: IChangeFilePositionPayload) =>
        dispatch({
            type: "CHANGE_FILE_POSITION",
            formId,
            value: payload,
        }),
    _setErrorMessages: (formId: string, payload: ISetErrorMessagePayload) =>
        dispatch({
            type: "SET_ERROR_MESSAGE",
            formId,
            value: payload,
        }),
    _resetForm: (formId: string, payload: IResetFormPayload) =>
        dispatch({type: "RESET_FORM", formId, value: payload}),
    _updateFieldLabel: (
        formId: string,
        translations: ITranslations["productForm"]
    ) =>
        dispatch({
            type: "CHANGE_FIELD_LABEL",
            formId,
            value: {
                comTreeState: translations.comTreeLabel,
                techTreeState: translations.techTreeLabel,
                comNode: translations.comTreeLabel,
                techNode: translations.techTreeLabel,
                ean: translations.productCodeLabel,
                label: translations.productNameLabel,
                pictures: translations.addImageButtonLabel,
                price: translations.productPriceLabel,
                manufacturerRef: translations.productReferenceManufacturerLabel,
                supplierRef: translations.productReferenceLabel,
                buyingReasons: translations.productBuyingReasons,
                purchaseLabel: translations.productPurchaseLabel,
                purchaseIntroduction: translations.productPurchaseIntroduction,
                activated: translations.productActivated,
                isTopRef: translations.isTopRef,
                declined: translations.declined,
                customsCode: translations.productCustomsCodeLabel,
                vatRate: translations.productVatRateLabel,
                originCountry: translations.productCountryLabel,
                minOrder: translations.productMinOrderLabel,
                assemblyTime: translations.productAssemblyTimeLabel,
            },
        })
})

export type ProductFormActions = ReturnType<typeof getActions>

interface IStoreProductArguments {
    product: ISupplierReference
    comRawData: ITreeNode[]
    techRawData: ITreeNode[]
}

interface IStoreDeclinedArguments {
    topRef: ISupplierReference
    declinedDroopyId: string
}
